import { useState, useRef } from 'react';
import clsx from 'clsx';
import { useButton } from 'react-aria';
import { Minus, Plus } from '@hungryroot/icons';
import { Heading } from '@hungryroot/ramen';
import {
  type PairingV1 as Pairing,
  type ProductV1 as Product,
} from '@hungryroot/rest';
import styles from './AddRemoveButton.module.css';

interface Props {
  ariaLabel?: string;
  addProduct?: (recipe: Pairing | Product) => void;
  removeProduct?: (recipe: Pairing | Product) => void;
  productData: Pairing | Product;
  isNotAddable: () => boolean;
  cartCount: number;
  onAddToCart?: () => void;
  testid?: string;
}

export default function AddRemoveButton(props: Props) {
  const {
    addProduct,
    removeProduct,
    productData,
    isNotAddable,
    cartCount,
    onAddToCart,
    testid,
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const { name: productName } = productData;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleDisable = () => {
    if (isNotAddable()) {
      document.getElementById(`${productData.id}-remove-button`)?.focus();
      return true;
    }
    return false;
  };

  const { buttonProps: countButtonProps } = useButton(
    {
      onPress: () => setIsExpanded(true),
      'aria-label': `${cartCount} in cart`,
    },
    buttonRef
  );

  const { buttonProps: addButtonProps } = useButton(
    {
      onPress: () => {
        if (addProduct) {
          addProduct(productData);
        }
        if (onAddToCart) onAddToCart();
        setIsExpanded(true);
      },
      isDisabled: handleDisable(),
      'aria-label': `Add ${productName} to cart`,
    },
    buttonRef
  );

  const { buttonProps: removeButtonProps } = useButton(
    {
      onPress: () => {
        if (removeProduct) {
          removeProduct(productData);
        }
      },
      'aria-label': `Remove ${productName} from cart`,
    },
    buttonRef
  );

  const renderAddRemoveButton = () => {
    if (!isExpanded && cartCount > 0) {
      return (
        <button
          {...countButtonProps}
          ref={buttonRef}
          className={`${styles['display-count-button-product-card']}`}
          data-testid={`${testid}-inactive-cart-count-button`}
        >
          <Heading testid={`${testid}-inactive-cart-count`} variant="H6">
            {cartCount}
          </Heading>
        </button>
      );
    }

    return (
      <div
        ref={wrapperRef}
        className={clsx(styles.container, {
          [styles['add-remove-button-wrapper']]: isExpanded && cartCount > 0,
        })}
        onBlur={(e) => {
          if (!wrapperRef.current?.contains(e.relatedTarget)) {
            setIsExpanded(false);
          }
        }}
        data-testid={testid}
      >
        {isExpanded && cartCount > 0 ? (
          <>
            <button
              {...removeButtonProps}
              id={`${productData.id}-remove-button`}
              className="shop-action-button"
              data-testid={`${testid}-remove-button`}
            >
              <Minus />
            </button>
            <Heading testid={`${testid}-active-cart-count`} variant="H6">
              {cartCount}
            </Heading>
          </>
        ) : null}
        <button
          {...addButtonProps}
          data-testid={`${testid}-add-button`}
          className={
            cartCount === 0 ? styles['add-only-button-product-card'] : ''
          }
        >
          <Plus />
        </button>
      </div>
    );
  };

  return renderAddRemoveButton();
}
